/**
 * Shrink a picked image in the browser before it is uploaded, so a 10 MB phone
 * photo or a huge logo file just works: anything over the size or pixel limits
 * is redrawn smaller; anything already small is sent untouched.
 *
 *   const file = await shrinkImage(picked, { maxEdge: 1600, keepAlpha: true });
 *
 * keepAlpha keeps transparency (PNG output, for logos and icons); otherwise the
 * result is a JPEG on a white background. If the browser cannot decode the file,
 * the original is returned and the server decides.
 */
const SKIP_BELOW_BYTES = 1.5 * 1024 * 1024;

const loadBitmap = (file) => {
  if (typeof createImageBitmap === 'function') return createImageBitmap(file);
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
};

const toBlob = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));

export async function shrinkImage(file, { maxEdge = 2400, keepAlpha = false, quality = 0.86 } = {}) {
  if (!file || !/^image\/(png|jpeg|webp)$/.test(file.type)) return file;
  let bitmap;
  try { bitmap = await loadBitmap(file); } catch (e) { return file; }
  const width = bitmap.width || bitmap.naturalWidth;
  const height = bitmap.height || bitmap.naturalHeight;
  if (!width || !height) return file;

  const scale = Math.min(1, maxEdge / Math.max(width, height));
  if (scale === 1 && file.size <= SKIP_BELOW_BYTES) return file;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!keepAlpha) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  if (bitmap.close) bitmap.close();

  let type = keepAlpha ? 'image/png' : 'image/jpeg';
  let blob = await toBlob(canvas, type, quality);
  // A PNG logo that is still large (a photographic logo): halve it until it fits, never below 600 px
  if (keepAlpha && blob && blob.size > 2.5 * 1024 * 1024) {
    let w = canvas.width; let h = canvas.height;
    while (blob.size > 2.5 * 1024 * 1024 && Math.max(w, h) > 600) {
      w = Math.round(w * 0.75); h = Math.round(h * 0.75);
      const c2 = document.createElement('canvas'); c2.width = w; c2.height = h;
      const c2x = c2.getContext('2d'); c2x.imageSmoothingQuality = 'high'; c2x.drawImage(canvas, 0, 0, w, h);
      blob = await toBlob(c2, type);
    }
  }
  if (!blob || blob.size >= file.size) return file; // never make it bigger
  const ext = type === 'image/png' ? '.png' : '.jpg';
  const name = (file.name || 'image').replace(/\.[^.]+$/, '') + ext;
  return new File([blob], name, { type, lastModified: Date.now() });
}

export default shrinkImage;
